import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { endpoints, formatApiError } from "../lib/api";
import { getAll, put, STORES } from "../lib/db";
import {
    ListChecks, PencilSimple, X, FloppyDisk, PaperPlaneTilt, Plus, Trash, Copy, CheckCircle, ChartBar,
} from "@phosphor-icons/react";
import { SUB_CATEGORIES } from "../lib/romEngine";

const TABS = [
    { key: "labour", label: "Schedule A · Labour" },
    { key: "consumables", label: "Schedule B · Consumables" },
    { key: "equipment", label: "Schedule C · Equipment" },
    { key: "subcontractors", label: "Schedule D · Subcontractors" },
    { key: "cleaning", label: "Schedule F · Cleaning" },
    { key: "conditions", label: "Conditions" },
    { key: "coefficients", label: "Engine Coefficients" },
];

const CONDITION_SECTIONS = [
    { key: "labour", label: "Labour conditions" },
    { key: "consumables", label: "Consumables conditions" },
    { key: "equipment", label: "Equipment conditions" },
    { key: "general", label: "General terms" },
    { key: "prime_area", label: "Prime Area" },
];

/* Numeric fields shown under each conditions sub-section. Each entry pulls
 * from the flat conditions{} object so the engine still works. */
const NUMERIC_FIELDS = {
    labour: [
        ["ot_multiplier", "OT multiplier (×)"],
        ["holiday_multiplier", "Holiday multiplier (×)"],
        ["small_tools_pct", "Small tools % (0–1)"],
        ["emergency_service_fee_std", "Emergency fee · standard ($)"],
        ["emergency_service_fee_prime", "Emergency fee · prime ($)"],
        ["prevailing_wage_markup", "Prevailing wage markup (×)"],
        ["specialty_trades_markup", "Specialty trades markup (×)"],
        ["subcontracted_general_markup", "Subcontracted GL markup (×)"],
        ["stand_by_min_hours", "Stand-by min hours"],
    ],
    consumables: [
        ["consumables_default_pct", "Default consumables % (0–1)"],
    ],
    equipment: [
        ["overhead_profit_markup", "Overhead + Profit markup (×)"],
        ["cat_offsite_pct", "CAT off-site logistical % (0–1)"],
    ],
    general: [
        ["per_diem_per_day", "Per diem ($/person/day)"],
        ["permits_markup", "Permits markup (×)"],
        ["bonding_pct", "Bonding % (0–1)"],
        ["mobilisation_distance_threshold_miles", "Mobilisation distance threshold (mi)"],
        ["rom_confidence_band", "ROM confidence band ± (0–1)"],
    ],
    prime_area: [],
};

export default function RateSchedule() {
    const { user } = useAuth();
    const canEdit = user?.role === "manager" || user?.role === "admin";
    const [tab, setTab] = useState("labour");
    const [sch, setSch] = useState(null);
    const [draft, setDraft] = useState(null);
    const [editing, setEditing] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");
    const [showSend, setShowSend] = useState(false);
    const [sentDoc, setSentDoc] = useState(null);
    const [leaderboard, setLeaderboard] = useState(null);
    const [cleaningAcc, setCleaningAcc] = useState(null);

    useEffect(() => { load(); }, []);
    async function load() {
        try {
            const { data } = await endpoints.activeRateSchedule();
            setSch(data);
            await put(STORES.rate_schedules, data);
        } catch {
            const all = await getAll(STORES.rate_schedules);
            setSch(all.find(s => s.is_active) || all[0] || null);
        }
        try {
            const { data } = await endpoints.vendorLeaderboard();
            setLeaderboard(data);
        } catch { /* offline / optional */ }
        try {
            const { data } = await endpoints.cleaningAccuracy();
            setCleaningAcc(data);
        } catch { /* offline / optional */ }
    }

    function startEdit() {
        setDraft(JSON.parse(JSON.stringify(sch)));
        setEditing(true);
        setErr(""); setMsg("");
    }
    function cancel() { setEditing(false); setDraft(null); }

    async function saveVersion() {
        try {
            const { data } = await endpoints.saveRateSchedule({
                schedule_a: draft.schedule_a,
                schedule_b: draft.schedule_b,
                schedule_c: draft.schedule_c,
                schedule_d: draft.schedule_d,
                schedule_f: draft.schedule_f,
                conditions: draft.conditions,
                conditions_text: draft.conditions_text,
                coefficients: draft.coefficients,
                is_active: true,
                effective_date: draft.effective_date,
                name: draft.name,
            });
            setSch(data);
            setEditing(false);
            setMsg(`Saved as v${data.version} (active)`);
            setTimeout(() => setMsg(""), 4000);
        } catch (e) {
            setErr(formatApiError(e));
        }
    }

    if (!sch) return <div className="max-w-[1600px] mx-auto px-6 py-8 text-[var(--rom-text-2)]">No rate schedule loaded.</div>;
    const current = editing ? draft : sch;

    return (
        <div className="max-w-[1600px] mx-auto px-6 py-8">
            <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">
                        Rate Schedule · 2026 T&M
                    </div>
                    <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                        <ListChecks size={28} weight="bold" className="text-[var(--rom-orange)]" />
                        {editing ? <input className="input text-2xl font-display font-black max-w-md" value={current.name} onChange={(e) => setDraft({...draft, name: e.target.value})} data-testid="rs-name" /> : sch.name}
                    </h1>
                    <div className="text-sm text-[var(--rom-text-2)] mt-1 flex items-center gap-3">
                        v{sch.version} · effective {editing
                            ? <input className="input text-sm py-1 max-w-[160px]" type="date" value={current.effective_date} onChange={(e) => setDraft({...draft, effective_date: e.target.value})} />
                            : sch.effective_date}
                    </div>
                    {sch.calibration_note && (
                        <div className="mt-2 flex items-start gap-2 text-xs text-[var(--rom-text-2)] bg-[var(--rom-orange-soft)]/40 border border-[var(--rom-orange)]/30 rounded px-2.5 py-1.5 max-w-xl" data-testid="rs-calibration-note">
                            <ChartBar size={13} weight="bold" className="text-[var(--rom-orange-dark)] shrink-0 mt-0.5" />
                            <span><b className="font-semibold">v{sch.version} calibration:</b> {sch.calibration_note}</span>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {msg && <span className="chip chip-green" data-testid="rs-msg">{msg}</span>}
                    {err && <span className="chip chip-orange" data-testid="rs-err">{err}</span>}
                    {!canEdit && <span className="chip chip-gray">View-only · Estimator</span>}
                    {canEdit && !editing && (
                        <>
                            <button className="btn btn-ghost" onClick={() => setShowSend(true)} data-testid="rs-send">
                                <PaperPlaneTilt size={14} weight="bold" /> Send for signature
                            </button>
                            <button className="btn btn-secondary" onClick={startEdit} data-testid="rs-edit">
                                <PencilSimple size={14} weight="bold" /> Edit & version up
                            </button>
                        </>
                    )}
                    {editing && (
                        <>
                            <button className="btn btn-ghost" onClick={cancel}><X size={14} /> Cancel</button>
                            <button className="btn btn-primary" onClick={saveVersion} data-testid="rs-save">
                                <FloppyDisk size={14} weight="bold" /> Save as v{sch.version + 1}
                            </button>
                        </>
                    )}
                </div>
            </header>

            <div className="flex flex-wrap gap-1.5 mb-4 border-b border-[var(--rom-border)]">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        data-testid={`rs-tab-${t.key}`}
                        className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                            tab === t.key ? "border-[var(--rom-orange)] text-[var(--rom-text)]" : "border-transparent text-[var(--rom-text-2)] hover:text-[var(--rom-text)]"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="rom-card">
                {tab === "labour" && (
                    <LabourTable
                        rows={current.schedule_a}
                        editable={editing}
                        onChange={(rows) => setDraft({...draft, schedule_a: rows})}
                    />
                )}
                {tab === "consumables" && (
                    <ConsumablesTable
                        rows={current.schedule_b}
                        editable={editing}
                        onChange={(rows) => setDraft({...draft, schedule_b: rows})}
                    />
                )}
                {tab === "equipment" && (
                    <EquipmentTable
                        rows={current.schedule_c}
                        editable={editing}
                        onChange={(rows) => setDraft({...draft, schedule_c: rows})}
                    />
                )}
                {tab === "subcontractors" && (
                    <SubcontractorTable
                        rows={current.schedule_d || []}
                        editable={editing}
                        leaderboard={leaderboard}
                        onChange={(rows) => setDraft({...draft, schedule_d: rows})}
                    />
                )}
                {tab === "cleaning" && (
                    <CleaningTable
                        rows={current.schedule_f || []}
                        editable={editing}
                        accuracy={cleaningAcc}
                        onChange={(rows) => setDraft({...draft, schedule_f: rows})}
                    />
                )}
                {tab === "conditions" && (
                    <ConditionsEditor
                        current={current}
                        editing={editing}
                        onChange={(patch) => setDraft({...draft, ...patch})}
                    />
                )}
                {tab === "coefficients" && (
                    <CoefficientsEditor
                        current={current}
                        editing={editing}
                        onChange={(coeff) => setDraft({...draft, coefficients: coeff})}
                    />
                )}
            </div>

            {showSend && (
                <SendForSignatureModal
                    schedule={sch}
                    onClose={() => setShowSend(false)}
                    onSent={(doc) => { setShowSend(false); setSentDoc(doc); }}
                />
            )}

            {sentDoc && (
                <SentLinkModal doc={sentDoc} onClose={() => setSentDoc(null)} />
            )}
        </div>
    );
}

/* ---------------- Labour ---------------- */
function LabourTable({ rows, editable, onChange }) {
    const update = (i, k, v) => onChange(rows.map((r, idx) => idx === i ? { ...r, [k]: (k === "role" ? v : Number(v)) } : r));
    const addRow = () => onChange([...rows, { role: "New role", standard: 0, prime: 0, ot_mult: 1.5 }]);
    const delRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
    return (
        <>
            <table className="rom-table" data-testid="rs-labour-table">
                <thead><tr><th>Role</th><th className="numeric">Standard $/hr</th><th className="numeric">Prime $/hr</th><th className="numeric">OT mult</th>{editable && <th></th>}</tr></thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i}>
                            <td>{editable
                                ? <input className="input py-1 px-2 text-sm" value={r.role} onChange={(e) => update(i, "role", e.target.value)} />
                                : <span className="font-medium">{r.role}</span>}</td>
                            <td className="numeric">{editable
                                ? <input className="input font-mono text-right py-1 px-2" type="number" step="0.01" value={r.standard} onChange={(e) => update(i, "standard", e.target.value)} />
                                : `$${Number(r.standard).toFixed(2)}`}</td>
                            <td className="numeric">{editable
                                ? <input className="input font-mono text-right py-1 px-2" type="number" step="0.01" value={r.prime} onChange={(e) => update(i, "prime", e.target.value)} />
                                : `$${Number(r.prime).toFixed(2)}`}</td>
                            <td className="numeric">{editable
                                ? <input className="input font-mono text-right py-1 px-2" type="number" step="0.1" value={r.ot_mult} onChange={(e) => update(i, "ot_mult", e.target.value)} />
                                : `${r.ot_mult}×`}</td>
                            {editable && <td><button onClick={() => delRow(i)} className="text-[var(--rom-danger)] hover:opacity-70" title="Remove row"><Trash size={14} /></button></td>}
                        </tr>
                    ))}
                </tbody>
            </table>
            {editable && <div className="p-3 border-t border-[var(--rom-border)]"><button onClick={addRow} className="btn btn-ghost btn-sm"><Plus size={12} weight="bold" /> Add role</button></div>}
        </>
    );
}

/* ---------------- Consumables ---------------- */
function ConsumablesTable({ rows, editable, onChange }) {
    const update = (i, k, v) => onChange(rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
    const addRow = () => onChange([...rows, { item: "New consumable", unit: "Each" }]);
    const delRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
    return (
        <>
            <table className="rom-table" data-testid="rs-consumables-table">
                <thead><tr><th>Item</th><th>Unit</th>{editable && <th></th>}</tr></thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i}>
                            <td>{editable ? <input className="input py-1 px-2 text-sm" value={r.item} onChange={(e) => update(i, "item", e.target.value)} /> : r.item}</td>
                            <td>{editable ? <input className="input py-1 px-2 text-sm max-w-[140px]" value={r.unit} onChange={(e) => update(i, "unit", e.target.value)} /> : r.unit}</td>
                            {editable && <td><button onClick={() => delRow(i)} className="text-[var(--rom-danger)] hover:opacity-70"><Trash size={14} /></button></td>}
                        </tr>
                    ))}
                </tbody>
            </table>
            {editable && <div className="p-3 border-t border-[var(--rom-border)]"><button onClick={addRow} className="btn btn-ghost btn-sm"><Plus size={12} weight="bold" /> Add consumable</button></div>}
        </>
    );
}

/* ---------------- Equipment ---------------- */
function EquipmentTable({ rows, editable, onChange }) {
    const update = (i, k, v) => onChange(rows.map((r, idx) => idx === i ? { ...r, [k]: (k === "rate_per_day" ? Number(v) : v) } : r));
    const addRow = () => onChange([...rows, { item: "New equipment", rate_per_day: 0, unit: "Each/Day" }]);
    const delRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
    return (
        <>
            <table className="rom-table" data-testid="rs-equipment-table">
                <thead><tr><th>Item</th><th className="numeric">$/day</th><th>Unit</th>{editable && <th></th>}</tr></thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i}>
                            <td>{editable ? <input className="input py-1 px-2 text-sm" value={r.item} onChange={(e) => update(i, "item", e.target.value)} /> : r.item}</td>
                            <td className="numeric">{editable
                                ? <input className="input font-mono text-right py-1 px-2" type="number" step="0.01" value={r.rate_per_day} onChange={(e) => update(i, "rate_per_day", e.target.value)} />
                                : `$${Number(r.rate_per_day).toFixed(2)}`}</td>
                            <td>{editable ? <input className="input py-1 px-2 text-sm max-w-[160px]" value={r.unit} onChange={(e) => update(i, "unit", e.target.value)} /> : r.unit}</td>
                            {editable && <td><button onClick={() => delRow(i)} className="text-[var(--rom-danger)] hover:opacity-70"><Trash size={14} /></button></td>}
                        </tr>
                    ))}
                </tbody>
            </table>
            {editable && <div className="p-3 border-t border-[var(--rom-border)]"><button onClick={addRow} className="btn btn-ghost btn-sm"><Plus size={12} weight="bold" /> Add equipment</button></div>}
        </>
    );
}

/* ---------------- Schedule F · Commercial Cleaning ---------------- */
const CLEANING_KINDS = [
    { v: "surface", label: "Surface (per sq ft)" },
    { v: "antimicrobial", label: "Anti-microbial (per sq ft)" },
    { v: "atp", label: "ATP test (per each)" },
    { v: "unit", label: "Unit / opening (per each)" },
    { v: "labour", label: "Labour (per hour)" },
    { v: "equipment", label: "Equipment / filter" },
    { v: "consumable", label: "Consumable (PPE)" },
];
function CleaningTable({ rows, editable, onChange, accuracy }) {
    const update = (i, k, v) => onChange(rows.map((r, idx) => idx === i ? { ...r, [k]: (k === "rate" ? Number(v) : v) } : r));
    const addRow = () => onChange([...rows, { item: "New cleaning item", rate: 0, unit: "$/EA", kind: "unit" }]);
    const delRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
    const acc = accuracy && accuracy.jobs_count > 0 ? accuracy : null;
    const factorPct = acc?.suggested_rate_factor != null ? Math.round((acc.suggested_rate_factor - 1) * 1000) / 10 : null;
    return (
        <>
            <div className="px-4 pt-3 pb-2 text-xs text-[var(--rom-text-2)] border-b border-[var(--rom-border)]">
                Standalone <b>Commercial Cleaning</b> catalog (Encircle-priced unit rates). Powers the <b>Cleaning</b> estimate mode. These rates already carry margin, so cleaning lines default to 0% markup. Edit per franchise as needed.
            </div>
            {acc && (
                <div className="p-4 border-b border-[var(--rom-border)]" data-testid="cleaning-accuracy-scorecard">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-[var(--rom-text-3)] font-semibold mb-2 flex items-center gap-1.5">
                        <ChartBar size={13} weight="bold" /> Cleaning quote accuracy · quoted vs invoiced ({acc.jobs_count} job{acc.jobs_count === 1 ? "" : "s"})
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2">
                        <div className="p-2.5 rounded-[4px] border border-[var(--rom-border)]">
                            <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)]">Avg error (MAE)</div>
                            <div className="font-mono font-bold text-lg tabular" data-testid="cleaning-acc-mae">{acc.mae_pct != null ? `${acc.mae_pct}%` : "—"}</div>
                        </div>
                        <div className="p-2.5 rounded-[4px] border border-[var(--rom-border)]">
                            <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)]">Bias</div>
                            <div className={`font-mono font-bold text-lg tabular ${acc.bias_pct > 0 ? "text-[var(--rom-orange-dark)]" : acc.bias_pct < 0 ? "text-[var(--rom-green)]" : ""}`} data-testid="cleaning-acc-bias">{acc.bias_pct != null ? `${acc.bias_pct > 0 ? "+" : ""}${acc.bias_pct}%` : "—"}</div>
                        </div>
                        <div className="p-2.5 rounded-[4px] border border-[var(--rom-border)]">
                            <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)]">Suggested rate factor</div>
                            <div className="font-mono font-bold text-lg tabular" data-testid="cleaning-acc-factor">{acc.suggested_rate_factor != null ? `×${acc.suggested_rate_factor}` : "—"}</div>
                        </div>
                    </div>
                    {factorPct != null && Math.abs(factorPct) >= 1 && (
                        <div className="text-[11px] text-[var(--rom-text-2)] bg-[var(--rom-orange-soft)]/40 border border-[var(--rom-orange)]/30 rounded px-2.5 py-1.5">
                            Cleaning quotes run {acc.bias_pct > 0 ? "below" : "above"} the final invoice by ~{Math.abs(acc.bias_pct)}% on average. Consider nudging Schedule F rates by <b>{factorPct > 0 ? "+" : ""}{factorPct}%</b> (×{acc.suggested_rate_factor}) to close the gap. Tracked separately from restoration calibration.
                        </div>
                    )}
                    <div className="text-[10px] text-[var(--rom-text-3)] mt-2">Bias &gt; 0 = invoiced above quote (under-quoting) · &lt; 0 = invoiced below quote. Record cleaning-job invoices to grow this.</div>
                </div>
            )}
            <table className="rom-table" data-testid="rs-cleaning-table">
                <thead><tr><th>Item</th><th className="numeric">Rate</th><th>Unit</th><th>Type</th>{editable && <th></th>}</tr></thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i}>
                            <td>{editable ? <input className="input py-1 px-2 text-sm" value={r.item} onChange={(e) => update(i, "item", e.target.value)} /> : r.item}</td>
                            <td className="numeric">{editable
                                ? <input className="input font-mono text-right py-1 px-2" type="number" step="0.01" value={r.rate} onChange={(e) => update(i, "rate", e.target.value)} />
                                : `$${Number(r.rate).toFixed(2)}`}</td>
                            <td>{editable ? <input className="input py-1 px-2 text-sm max-w-[120px]" value={r.unit} onChange={(e) => update(i, "unit", e.target.value)} /> : r.unit}</td>
                            <td>{editable
                                ? <select className="select py-1 px-2 text-sm" value={r.kind || "unit"} onChange={(e) => update(i, "kind", e.target.value)}>{CLEANING_KINDS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}</select>
                                : (CLEANING_KINDS.find((k) => k.v === r.kind) || {}).label || r.kind}</td>
                            {editable && <td><button onClick={() => delRow(i)} className="text-[var(--rom-danger)] hover:opacity-70"><Trash size={14} /></button></td>}
                        </tr>
                    ))}
                </tbody>
            </table>
            {editable && <div className="p-3 border-t border-[var(--rom-border)]"><button onClick={addRow} className="btn btn-ghost btn-sm"><Plus size={12} weight="bold" /> Add cleaning item</button></div>}
        </>
    );
}

/* ---------------- Subcontractors (Schedule D — vendor roster) ---------------- */
function SubcontractorTable({ rows, editable, onChange, leaderboard }) {
    const numKeys = new Set(["rate", "default_markup_pct"]);
    const update = (i, k, v) => onChange(rows.map((r, idx) => idx === i ? { ...r, [k]: (numKeys.has(k) ? Number(v) : v) } : r));
    const addRow = () => onChange([...rows, { item: "New vendor / service", category: "trade", rate: 0, unit: "$/hr", default_markup_pct: 0.20, vendor: "" }]);
    const delRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
    const catLabel = (c) => (SUB_CATEGORIES.find((x) => x.v === c) || {}).label || c;
    const stats = useMemo(() => {
        const m = {};
        (leaderboard?.vendors || []).forEach((v) => { m[v.item] = v; });
        return m;
    }, [leaderboard]);
    const ranked = (leaderboard?.vendors || []).filter((v) => v.variance_pct != null);
    return (
        <>
            <div className="px-4 pt-3 pb-2 text-xs text-[var(--rom-text-2)] border-b border-[var(--rom-border)]">
                Managed roster of subs/vendors. These power the <b>“Add from roster”</b> picker in ROM Section D. Tag rental equipment booked through a sub as <b>Rental Equipment via Sub</b> so it stays visible.
            </div>

            {ranked.length > 0 && (
                <div className="p-4 border-b border-[var(--rom-border)]" data-testid="vendor-scorecard">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-[var(--rom-text-3)] font-semibold mb-2 flex items-center gap-1.5">
                        <ChartBar size={13} weight="bold" /> Vendor scorecard · quoted vs actual ({leaderboard.jobs_with_vendor_actuals} job{leaderboard.jobs_with_vendor_actuals === 1 ? "" : "s"})
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {ranked.slice(0, 6).map((v) => (
                            <div key={v.item} className="flex items-center justify-between gap-2 p-2.5 rounded-[4px] border border-[var(--rom-border)]" data-testid={`scorecard-${v.item}`}>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium truncate">{v.vendor || v.item}</div>
                                    <div className="text-[10px] text-[var(--rom-text-3)] truncate">{v.vendor ? v.item : catLabel(v.category)} · {v.jobs} job{v.jobs === 1 ? "" : "s"}</div>
                                </div>
                                <span className={`chip shrink-0 ${v.direction === "over" ? "chip-orange" : v.direction === "under" ? "chip-green" : "chip-gray"}`} style={{ fontVariantNumeric: "tabular-nums" }}>
                                    {v.variance_pct > 0 ? "+" : ""}{v.variance_pct}%
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className="text-[10px] text-[var(--rom-text-3)] mt-2">Over = invoiced above quote · Under = came in below quote. Record Tier-B actuals with per-vendor figures to grow this.</div>
                </div>
            )}

            <table className="rom-table" data-testid="rs-subcontractors-table">
                <thead><tr><th>Item / Service</th><th>Vendor</th><th>Category</th><th className="numeric">Rate</th><th>Unit</th><th className="numeric">Markup %</th><th className="numeric">Quote acc.</th>{editable && <th></th>}</tr></thead>
                <tbody>
                    {rows.map((r, i) => {
                        const st = stats[r.item];
                        return (
                        <tr key={i} data-testid={`rs-sub-row-${i}`}>
                            <td>{editable
                                ? <input className="input py-1 px-2 text-sm" value={r.item} onChange={(e) => update(i, "item", e.target.value)} />
                                : <span className="font-medium">{r.item}</span>}</td>
                            <td>{editable
                                ? <input className="input py-1 px-2 text-sm max-w-[160px]" value={r.vendor || ""} placeholder="—" onChange={(e) => update(i, "vendor", e.target.value)} />
                                : <span className="text-[var(--rom-text-2)]">{r.vendor || "—"}</span>}</td>
                            <td>{editable
                                ? <select className="select py-1 px-2 text-sm" value={r.category || "trade"} onChange={(e) => update(i, "category", e.target.value)}>
                                    {SUB_CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                                  </select>
                                : <span className="chip chip-gray">{catLabel(r.category)}</span>}</td>
                            <td className="numeric">{editable
                                ? <input className="input font-mono text-right py-1 px-2" type="number" step="0.01" value={r.rate} onChange={(e) => update(i, "rate", e.target.value)} />
                                : `$${Number(r.rate).toFixed(2)}`}</td>
                            <td>{editable
                                ? <input className="input py-1 px-2 text-sm max-w-[120px]" value={r.unit || ""} onChange={(e) => update(i, "unit", e.target.value)} />
                                : r.unit}</td>
                            <td className="numeric">{editable
                                ? <input className="input font-mono text-right py-1 px-2 max-w-[90px]" type="number" step="0.01" value={r.default_markup_pct ?? 0.20} onChange={(e) => update(i, "default_markup_pct", e.target.value)} />
                                : `${Math.round((r.default_markup_pct ?? 0.20) * 100)}%`}</td>
                            <td className="numeric">
                                {st && st.variance_pct != null
                                    ? <span className={`chip ${st.direction === "over" ? "chip-orange" : st.direction === "under" ? "chip-green" : "chip-gray"}`} title={`${st.jobs} job(s) · quoted ${st.total_quoted} → actual ${st.total_actual}`} data-testid={`rs-sub-acc-${i}`}>{st.variance_pct > 0 ? "+" : ""}{st.variance_pct}%</span>
                                    : <span className="text-[var(--rom-text-3)] text-xs">—</span>}
                            </td>
                            {editable && <td><button onClick={() => delRow(i)} className="text-[var(--rom-danger)] hover:opacity-70" title="Remove row"><Trash size={14} /></button></td>}
                        </tr>
                    );})}
                    {rows.length === 0 && (
                        <tr><td colSpan={editable ? 8 : 7} className="text-center text-[var(--rom-text-3)] py-6">No vendors in the roster yet.</td></tr>
                    )}
                </tbody>
            </table>
            {editable && <div className="p-3 border-t border-[var(--rom-border)]"><button onClick={addRow} className="btn btn-ghost btn-sm" data-testid="rs-sub-add"><Plus size={12} weight="bold" /> Add vendor / service</button></div>}
        </>
    );
}

/* ---------------- Conditions ---------------- */
function ConditionsEditor({ current, editing, onChange }) {
    const [section, setSection] = useState("labour");
    const conds = current.conditions || {};
    const text = current.conditions_text || {};

    const setNum = (key, v) => onChange({
        conditions: { ...conds, [key]: Number(v) },
    });
    const setText = (sec, idx, v) => {
        const arr = [...(text[sec] || [])];
        arr[idx] = v;
        onChange({ conditions_text: { ...text, [sec]: arr } });
    };
    const addPara = (sec) => onChange({
        conditions_text: { ...text, [sec]: [...(text[sec] || []), "New clause — describe the condition here."] },
    });
    const delPara = (sec, idx) => {
        const arr = (text[sec] || []).filter((_, i) => i !== idx);
        onChange({ conditions_text: { ...text, [sec]: arr } });
    };
    const setPrime = (v) => onChange({ conditions_text: { ...text, prime_area: v } });
    const setFooter = (v) => onChange({ conditions_text: { ...text, footer_note: v } });

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[220px,1fr]" data-testid="rs-conditions">
            <aside className="border-r border-[var(--rom-border)] p-2 bg-slate-50 lg:rounded-l-[6px]">
                {CONDITION_SECTIONS.map((s) => (
                    <button
                        key={s.key}
                        onClick={() => setSection(s.key)}
                        data-testid={`cond-section-${s.key}`}
                        className={`w-full text-left px-3 py-2 text-sm font-semibold rounded-[4px] mb-1 transition-colors ${
                            section === s.key ? "bg-[var(--rom-navy)] text-white" : "text-[var(--rom-text)] hover:bg-white"
                        }`}
                    >
                        {s.label}
                        <div className="text-[10px] uppercase tracking-wider opacity-70 mt-0.5">
                            {s.key === "prime_area"
                                ? "Single paragraph"
                                : `${(text[s.key] || []).length} clauses · ${(NUMERIC_FIELDS[s.key] || []).length} numeric`}
                        </div>
                    </button>
                ))}
            </aside>

            <section className="p-5 space-y-6">
                {section !== "prime_area" && (NUMERIC_FIELDS[section] || []).length > 0 && (
                    <div>
                        <h4 className="font-display font-bold uppercase text-xs tracking-wider mb-3 text-[var(--rom-text-2)]">Numeric values</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {(NUMERIC_FIELDS[section] || []).map(([k, label]) => (
                                <NumericRow key={k}
                                    label={label} value={conds[k]} editing={editing}
                                    onChange={(v) => setNum(k, v)} testId={`cond-num-${k}`}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {section === "prime_area" ? (
                    <div>
                        <h4 className="font-display font-bold uppercase text-xs tracking-wider mb-3 text-[var(--rom-text-2)]">Prime Area definition</h4>
                        {editing ? (
                            <textarea
                                className="textarea min-h-[200px]" rows={8}
                                value={text.prime_area || ""}
                                onChange={(e) => setPrime(e.target.value)}
                                data-testid="cond-prime-text"
                            />
                        ) : (
                            <p className="text-sm leading-relaxed bg-slate-50 p-4 rounded-[4px] border border-[var(--rom-border)]">{text.prime_area || "—"}</p>
                        )}
                        <h4 className="font-display font-bold uppercase text-xs tracking-wider mt-6 mb-3 text-[var(--rom-text-2)]">Footer note (appears at the end of the document)</h4>
                        {editing ? (
                            <textarea
                                className="textarea" rows={2}
                                value={text.footer_note || ""}
                                onChange={(e) => setFooter(e.target.value)}
                                data-testid="cond-footer-text"
                            />
                        ) : (
                            <p className="text-sm leading-relaxed bg-slate-50 p-4 rounded-[4px] border border-[var(--rom-border)] italic">{text.footer_note || "—"}</p>
                        )}
                    </div>
                ) : (
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="font-display font-bold uppercase text-xs tracking-wider text-[var(--rom-text-2)]">Narrative clauses</h4>
                            {editing && (
                                <button onClick={() => addPara(section)} className="btn btn-ghost btn-sm" data-testid={`cond-add-${section}`}>
                                    <Plus size={12} weight="bold" /> Add clause
                                </button>
                            )}
                        </div>
                        <div className="space-y-3">
                            {(text[section] || []).length === 0 && <div className="text-sm text-[var(--rom-text-3)]">No clauses yet.</div>}
                            {(text[section] || []).map((p, idx) => (
                                <div key={idx} className="relative">
                                    {editing ? (
                                        <>
                                            <textarea
                                                className="textarea pr-9" rows={Math.max(2, Math.ceil((p || "").length / 110))}
                                                value={p}
                                                onChange={(e) => setText(section, idx, e.target.value)}
                                                data-testid={`cond-${section}-${idx}`}
                                            />
                                            <button onClick={() => delPara(section, idx)}
                                                className="absolute top-2 right-2 text-[var(--rom-danger)] hover:opacity-70" title="Remove clause">
                                                <Trash size={14} />
                                            </button>
                                        </>
                                    ) : (
                                        <p className="text-sm leading-relaxed bg-slate-50 p-3 rounded-[4px] border border-[var(--rom-border)]">{p}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}

function NumericRow({ label, value, editing, onChange, testId }) {
    return (
        <div className="flex items-center justify-between gap-3 p-2 rounded bg-slate-50 border border-[var(--rom-border)]">
            <span className="text-[var(--rom-text-2)] text-xs">{label}</span>
            {editing
                ? <input className="input font-mono text-right py-1 px-2 max-w-[140px]" type="number" step="0.01" value={value ?? 0} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
                : <span className="font-mono font-semibold">{typeof value === "number" ? value : "—"}</span>}
        </div>
    );
}

/* ---------------- Coefficients (sq-ft engine) ---------------- */
function CoefficientsEditor({ current, editing, onChange }) {
    const coeff = current.coefficients || {};
    const setPath = (path, v) => {
        const next = JSON.parse(JSON.stringify(coeff));
        let o = next;
        for (let i = 0; i < path.length - 1; i++) o = o[path[i]];
        o[path[path.length - 1]] = Number(v);
        onChange(next);
    };
    return (
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Group title="Labour-hours per sq ft (by loss type)">
                {Object.entries(coeff.labour_hours_per_sqft || {}).map(([k, v]) => (
                    <NumericRow key={k} label={k} value={v} editing={editing} onChange={(nv) => setPath(["labour_hours_per_sqft", k], nv)} />
                ))}
            </Group>
            <Group title="Scope multipliers">
                {Object.entries(coeff.scope_multiplier || {}).map(([k, v]) => (
                    <NumericRow key={k} label={k} value={v} editing={editing} onChange={(nv) => setPath(["scope_multiplier", k], nv)} />
                ))}
            </Group>
            <Group title="Water category multipliers">
                {Object.entries(coeff.water_category_multiplier || {}).map(([k, v]) => (
                    <NumericRow key={k} label={k} value={v} editing={editing} onChange={(nv) => setPath(["water_category_multiplier", k], nv)} />
                ))}
            </Group>
            <Group title="Water class multipliers">
                {Object.entries(coeff.water_class_multiplier || {}).map(([k, v]) => (
                    <NumericRow key={k} label={k} value={v} editing={editing} onChange={(nv) => setPath(["water_class_multiplier", k], nv)} />
                ))}
            </Group>
            <Group title="Equipment sq-ft ratios (sq ft per unit)">
                {Object.entries(coeff.equipment_ratios || {}).map(([k, v]) => (
                    <NumericRow key={k} label={k} value={v} editing={editing} onChange={(nv) => setPath(["equipment_ratios", k], nv)} />
                ))}
            </Group>
            <Group title="Labour composition (share of total hours)">
                {Object.entries(coeff.labour_composition || {}).map(([k, v]) => (
                    <NumericRow key={k} label={k} value={v} editing={editing} onChange={(nv) => setPath(["labour_composition", k], nv)} />
                ))}
            </Group>
            <Group title="Disposal factor by room cut height" unvalidated>
                {Object.entries(coeff.disposal_scope_factor || {}).map(([k, v]) => (
                    <NumericRow key={k} label={k} value={v} editing={editing} onChange={(nv) => setPath(["disposal_scope_factor", k], nv)} testId={`coeff-disposal-scope-${k}`} />
                ))}
            </Group>
            <Group title="Site condition labour uplift %" unvalidated>
                {Object.entries(coeff.site_condition_uplift_pct || {}).map(([k, v]) => (
                    <NumericRow key={k} label={k} value={v} editing={editing} onChange={(nv) => setPath(["site_condition_uplift_pct", k], nv)} testId={`coeff-site-condition-${k}`} />
                ))}
            </Group>
        </div>
    );
}

function Group({ title, children, unvalidated }) {
    return (
        <div>
            <h4 className="font-display font-bold uppercase text-xs tracking-wider mb-3 text-[var(--rom-text-2)] flex items-center gap-2">
                {title}
                {unvalidated && <span className="chip chip-orange text-[9px] normal-case tracking-normal font-semibold" data-testid="coeff-unvalidated-chip">unvalidated</span>}
            </h4>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

/* ---------------- Send for signature modal ---------------- */
function SendForSignatureModal({ schedule, onClose, onSent }) {
    const { user } = useAuth();
    const [form, setForm] = useState({
        recipient_name: "", recipient_email: "",
        project_address: "", franchise_legal_name: "",
        city_state_zip: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    async function submit() {
        setBusy(true);
        try {
            const { data } = await endpoints.sendRateSchedule(schedule.id, form);
            onSent(data);
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="send-modal">
            <div className="rom-card w-full max-w-xl">
                <div className="rom-card-head">
                    <h3><PaperPlaneTilt size={14} weight="bold" /> Send rate schedule for signature</h3>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                <div className="rom-card-body space-y-4">
                    <p className="text-sm text-[var(--rom-text-2)]">
                        Generates a share-link to the full rate schedule document. Your client can review every clause,
                        initial each section, and electronically sign — no account required.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        <FX label="Client / recipient name" v={form.recipient_name} on={(v) => setForm({...form, recipient_name: v})} testId="send-recipient-name" />
                        <FX label="Client email" v={form.recipient_email} on={(v) => setForm({...form, recipient_email: v})} testId="send-recipient-email" />
                        <FX label="Project address" v={form.project_address} on={(v) => setForm({...form, project_address: v})} className="col-span-2" />
                        <FX label="Franchise legal name" v={form.franchise_legal_name} on={(v) => setForm({...form, franchise_legal_name: v})} />
                        <FX label="City, State, Zip" v={form.city_state_zip} on={(v) => setForm({...form, city_state_zip: v})} />
                    </div>
                    {err && <div className="text-xs text-[var(--rom-danger)]">{err}</div>}
                    <div className="flex justify-end gap-2 pt-2">
                        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
                        <button className="btn btn-primary" onClick={submit} disabled={busy || !form.recipient_name} data-testid="send-submit">
                            <PaperPlaneTilt size={14} weight="bold" /> {busy ? "Sending…" : "Send"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function FX({ label, v, on, className = "", testId }) {
    return (
        <div className={className}>
            <label className="label">{label}</label>
            <input className="input" value={v || ""} onChange={(e) => on(e.target.value)} data-testid={testId} />
        </div>
    );
}

function SentLinkModal({ doc, onClose }) {
    const [copied, setCopied] = useState(false);
    const url = `${window.location.origin}/share/${doc.share_link}`;
    function copy() {
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }
    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="sent-link-modal">
            <div className="rom-card w-full max-w-xl">
                <div className="rom-card-head">
                    <h3><CheckCircle size={14} weight="fill" className="text-[var(--rom-green)]" /> Sent · share this link</h3>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                <div className="rom-card-body space-y-4">
                    <p className="text-sm text-[var(--rom-text-2)]">
                        Send this link to <b className="text-[var(--rom-text)]">{doc.payload?.recipient_name}</b>.
                        Anyone with the link can view the rate schedule and sign it — no account needed.
                        Track its status from the Documents board.
                    </p>
                    <div className="flex gap-2">
                        <input readOnly value={url} className="input font-mono text-xs" data-testid="share-link-input" />
                        <button className="btn btn-secondary" onClick={copy} data-testid="copy-link">
                            <Copy size={14} weight="bold" /> {copied ? "Copied!" : "Copy"}
                        </button>
                    </div>
                    <a href={url} target="_blank" rel="noreferrer" className="btn btn-ghost w-full justify-center" data-testid="open-share">
                        Open the recipient view in a new tab
                    </a>
                </div>
            </div>
        </div>
    );
}
